import React, { useEffect, useMemo } from "react";
import "react-datepicker/dist/react-datepicker.css";
import styles from "../datePicker/DatePicker.module.scss";
import DatePicker from "react-datepicker";
import { BUSINESS_HOURS } from "../../../constants/business-hours";
import {
  ceilToNextSlot,
  getBusinessEndTime,
  getTodayMinSlot,
  isOutsideBusinessHours,
  isToday,
  moveToNextBusinessDay,
  roundToNextSlot,
} from "../../../utils/utils";

type TimeSlot = {
  start: Date | null;
  end: Date | null;
};

export default function TimeSlotPicker({
  onChange,
  minTime,
  maxTime,
  min,
  max,
  placeholder,
}: {
  onChange: (slot: Date) => void;
  min?: string;
  max?: string;
  minTime?: Date;
  placeholder?: string;
  maxTime?: Date;
}) {
  const [isTouched, setIsTouched] = React.useState(false);
  const [slot, setSlot] = React.useState<TimeSlot>({
    start: null,
    end: null,
  });

  const displayLabel = useMemo(() => {
    if (!slot.start || !slot.end) return "";

    const pad = (n: number) => String(n).padStart(2, "0");

    const formatDateTime = (d: Date, withDate: boolean) => {
      const year = d.getFullYear();
      const month = pad(d.getMonth() + 1);
      const day = pad(d.getDate());
      const hours = pad(d.getHours());
      const minutes = pad(d.getMinutes());

      if (!withDate) return `${hours}:${minutes}`;

      return `${day}.${month}.${year} ${hours}:${minutes}`;
    };

    const startStr = formatDateTime(slot.start, true);
    const endStr = formatDateTime(slot.end, false);

    return `${startStr} - ${endStr}`;
  }, [slot.start, slot.end]);

  const CustomInput = React.forwardRef<HTMLInputElement, any>(
    ({ value, onClick }, ref) => (
      <input
        ref={ref}
        onClick={onClick}
        readOnly
        style={{ border: "1.5px solid #d1d1d1" }}
        className={styles.customDatePickerInput}
        value={displayLabel || value}
        placeholder={placeholder}
      />
    ),
  );

  const getEffectiveMinForDate = (selected: Date): Date => {
    const { START_TIME, END_TIME } = BUSINESS_HOURS;
    const now = new Date();

    // Business start/end for the selected day
    const businessStart = new Date(selected);
    businessStart.setHours(START_TIME, 0, 0, 0);

    const businessEnd = new Date(selected);
    businessEnd.setHours(END_TIME, 0, 0, 0);

    // Not today → earliest is business start
    if (selected.toDateString() !== now.toDateString()) {
      return businessStart;
    }

    // Today → start from "now", rounded to next slot
    let base = ceilToNextSlot(now);

    // Clamp to business start
    if (base < businessStart) {
      base = businessStart;
    }

    // Your pattern: +90min, then round up to next full hour
    let candidate = new Date(base.getTime() + 90 * 60 * 1000);
    if (candidate.getMinutes() !== 0) {
      candidate.setHours(candidate.getHours() + 1, 0, 0, 0);
    }

    // If we overflow business hours today → next day at business start
    if (candidate > businessEnd) {
      const next = new Date(selected);
      next.setDate(next.getDate() + 1);
      next.setHours(START_TIME, 0, 0, 0);
      return next;
    }

    return candidate;
  };

  const handleChange = (date: Date | null) => {
    if (!date) {
      setSlot({ start: null, end: null });
      return;
    }

    let selected = new Date(date);

    // -------------------------------------
    // 1. If today → enforce today-specific minimum slot
    // -------------------------------------
    if (isToday(selected)) {
      const todayMin = getTodayMinSlot(); // <-- USE IT HERE

      // If user clicked a time before the valid window, adjust it
      if (selected < todayMin) {
        selected = todayMin;
      }
    }

    // -------------------------------------
    // 2. If selected time is outside business hours → move to next business day
    // -------------------------------------
    if (isOutsideBusinessHours(selected)) {
      selected = moveToNextBusinessDay(selected);
    }

    // -------------------------------------
    // 3. Round to your slot (ceil to next 30)
    // -------------------------------------
    let start = ceilToNextSlot(selected);

    // -------------------------------------
    // 4. If still outside business hours → next business day start
    // -------------------------------------
    if (isOutsideBusinessHours(start)) {
      start = moveToNextBusinessDay(start);
    }

    // -------------------------------------
    // 5. Skip the disabled 10:00–11:00 slot
    // -------------------------------------
    if (start.getHours() === 10) {
      start.setHours(11, 0, 0, 0);
    }

    const end = getEndDate(start);
    setSlot({ start, end });
    onChange(start);
  };

  const getAvailableHour = (selected: Date) => {
    const h = selected.getHours();

    // Before business hours → use start time
    if (h < BUSINESS_HOURS.START_TIME) {
      return BUSINESS_HOURS.START_TIME;
    }

    // After business hours → use end time
    if (h > BUSINESS_HOURS.END_TIME) {
      return BUSINESS_HOURS.END_TIME;
    }

    // Otherwise → +2 hours (your rule)
    return h + 2;
  };

  const getEndDate = (start: Date) => {
    const end = new Date(start);
    end.setHours(end.getHours() + 1);
    return end;
  };

  const parseDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  };

  const minDate = min ? (parseDate(min) ?? undefined) : undefined;
  const maxDate = max ? (parseDate(max) ?? undefined) : undefined;

  const filterTime = (time: Date) => {
    // Disable 10:00–11:00 slot
    if (time.getHours() === 10) return false;

    // Use slot.start if selected, otherwise minDate (handles end-of-day → tomorrow case)
    const checkDate = slot.start || minDate || new Date();
    const endTime = getBusinessEndTime(checkDate);

    // Cap by business end time for the selected date (Saturday = 17, others = 19)
    if (time.getHours() > endTime) return false;

    // For future dates, all business hours are available
    if (!isToday(checkDate)) return true;

    // For today: disable past slots
    const todayMin = getTodayMinSlot();
    return time.getHours() >= todayMin.getHours();
  };

  // console.log("minDate: ", minDate);
  // console.log("minTime: ", minTime);

  useEffect(() => {
    const timer = setTimeout(() => {
      const items = document.querySelectorAll(
        ".react-datepicker__time-list-item",
      );

      const timeRegex = /^(\d{1,2}):(\d{2})$/;

      items.forEach((item) => {
        const original =
          item.getAttribute("data-original") || item.textContent?.trim();
        if (!original) return;

        const match = original.match(timeRegex);
        if (!match) return;

        const [_, hStr, mStr] = match;
        const h = Number(hStr);
        const m = Number(mStr);

        const pad = (n: number) => String(n).padStart(2, "0");

        const start = new Date();
        start.setHours(h, m, 0, 0);

        const end = new Date(start.getTime() + 60 * 60 * 1000);

        const label = `${pad(start.getHours())}:${pad(start.getMinutes())} - ${pad(
          end.getHours(),
        )}:${pad(end.getMinutes())}`;

        item.setAttribute("data-original", original);

        item.textContent = label;
      });
    }, 0);

    return () => clearTimeout(timer);
  }, [slot.start, isTouched]);

  return (
    <DatePicker
      onInputClick={() => {
        setIsTouched(!isTouched);
      }}
      selected={slot.start}
      onChange={(date) => {
        if (!date) return;
        handleChange(date);
      }}
      filterDate={(date) => date.getDay() !== 0}
      placeholderText={placeholder}
      showTimeSelect
      minDate={minDate}
      maxDate={maxDate}
      minTime={minTime}
      maxTime={maxTime}
      filterTime={filterTime}
      timeIntervals={60}
      dateFormat="HH:mm"
      popperClassName="time-slot-popper"
      timeFormat="HH:mm"
      customInput={<CustomInput />}
    />
  );
}
